/**
 * Comments API - Read Operations
 *
 * Fetching comments for posts.
 */

import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocsFromCache,
  getDocsFromServer,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { ensureFreshToken } from "../authUtils";
import { getFirestoreFetchPolicy, isLowQuotaMode } from "../firestoreFetchPolicy";
import { normalizeTimestamp, normalizeUserData, normalizeContentField } from "./normalize.js";

/**
 * Fetches a paginated page of comments for a post, respecting the configured
 * Firestore fetch policy (cache-first / network-only / cache-only).
 *
 * Soft-deleted comments (deletedAt != null) are intentionally included in the
 * result so that reply thread structure is preserved in the UI.
 *
 * @param {string} postId
 * @param {{
 *   pageSize?: number,
 *   cursor?: { id: string, timestamp: string } | null,
 *   fetchPolicy?: string,
 *   preferServer?: boolean,
 * }} opts
 *
 * `cursor` must be the `{ id, timestamp }` object returned by the previous
 * call. The timestamp is used directly with `startAfter()` to eliminate the
 * extra `getDoc` read that plain string cursors required.
 * @returns {Promise<Array>} Array of comment objects. Caller is responsible for
 *          building the `{ id, timestamp }` cursor from the last comment.
 */
export const getPostComments = async (
  postId,
  { pageSize = 3, cursor = null, fetchPolicy: inputPolicy, preferServer } = {},
) => {
  try {
    await ensureFreshToken();

    const commentsRef = collection(db, "comments");
    let q = query(
      commentsRef,
      where("postId", "==", postId),
      orderBy("timestamp", "desc"),
    );

    if (cursor) {
      // CURSOR-BASED PAGINATION: Use timestamp value directly with startAfter()
      // This eliminates the extra getDoc network round-trip per "Load more" click.
      // Cursor format: { id: string, timestamp: string (ISO) }
      q = query(q, startAfter(new Date(cursor.timestamp)), limit(pageSize));
    } else {
      q = query(q, limit(pageSize));
    }

    const lowQuota = isLowQuotaMode();
    const fetchPolicy = getFirestoreFetchPolicy(inputPolicy);

    let snapshot;

    // ── Cache-first / cache-only path ──
    if (fetchPolicy === "cache-only" || fetchPolicy === "cache-first") {
      try {
        const cachedSnap = await (typeof getDocsFromCache === "function"
          ? getDocsFromCache(q)
          : getDocs(q));
        if (cachedSnap && (!cachedSnap.empty || fetchPolicy === "cache-only")) {
          snapshot = cachedSnap;
        } else if (lowQuota || preferServer === false) {
          snapshot = cachedSnap; // may be empty — caller handles it
        }
      } catch (_) {
        // Cache miss → fall through to server
      }
    }

    // ── Network path ──
    if (!snapshot && fetchPolicy !== "cache-only") {
      try {
        snapshot = await (typeof getDocsFromServer === "function"
          ? getDocsFromServer(q)
          : getDocs(q));
      } catch {
        snapshot = await getDocs(q);
      }
    }

    return snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      const user = normalizeUserData(data);
      const normalizedUpdatedAt = data.updatedAt
        ? normalizeTimestamp(data.updatedAt)
        : null;
      return {
        id: docSnap.id,
        postId: data.postId,
        userId: data.userId || null,
        username: user.username,
        userProfession: user.profession || "",
        profession: user.profession || "",
        photoURL: user.photoURL || "",
        text: normalizeContentField(data),
        timestamp: normalizeTimestamp(data.timestamp),
        likes: data.likes || 0,
        isLiked: false,
        replyCount: data.replyCount || 0,
        // Soft-delete fields — present only on deleted comments
        deletedAt: data.deletedAt ? normalizeTimestamp(data.deletedAt) : null,
        updatedAt: normalizedUpdatedAt,
        isEdited: !!normalizedUpdatedAt,
      };
    });
  } catch (error) {
    console.error("Error fetching comments:", error);
    throw error;
  }
};
